<script>
import Layout from '@layouts/adminLayout.vue'
import MottoIcon from '@components/motto-icon.vue'
import AppCarousel from '@components/apps/apps-carousel.vue'
import AppBlock from '@components/apps/app-block.vue'
import CategorySelector from '@components/category-selector.vue'
import LoaderIcon from '@components/loader-icon.vue'
import { getAllApps } from '@src/api/apps'
import { transformMany as transformApps } from '@src/api/transformers/appTransformer'
import _ from 'lodash'
import { appsComputed, authComputed } from '@state/helpers'

export default {
  page: {
    title: 'Apps',
    meta: [
      {
        name: 'description',
        content: 'The Apps page.',
      },
    ],
  },
  components: {
    Layout,
    CategorySelector,
    AppCarousel,
    AppBlock,
    LoaderIcon,
    MottoIcon,
  },
  data() {
    return {
      loading: true,
      error: false,
      selectedCategory: '',
      gridView: false,
      gridViewLabel: 'about the apps',
      gridViewSelectedCategory: '',
      carouselView: true,
    }
  },
  computed: {
    ...appsComputed,
    ...authComputed,
    getDistinctCategories() {
      return _.chain(this.allApps)
        .map('category')
        .uniq()
        .filter((x) => x !== null && x !== '')
        .sortBy((category) => {
          return this.getCategoryOrder(category)
        })
        .value()
    },

    getSelectedCategories() {
      if (_.isEmpty(this.selectedCategory)) {
        return this.getDistinctCategories
      }

      return [this.selectedCategory]
    },

    filteredFavoriteApps() {
      return _.orderBy(
        this.favoriteApps.filter((app) => this.getSelectedCategories.includes(app.category)),
        ['favoritedAt']
      )
    },

    gridViewApps() {
      if (this.gridViewSelectedCategory === 'Favorites') {
        return this.favoriteApps
      }

      if (this.gridViewSelectedCategory) {
        return this.allApps.filter((app) => this.gridViewSelectedCategory === app.category)
      }

      if (this.gridViewLabel === 'about the apps') {
        return this.allApps.filter((app) => this.getSelectedCategories.includes(app.category))
      }

      return this.allApps
    },

    isAboutTheAppsSelected() {
      return this.gridViewLabel === 'about the apps'
    },
  },
  created() {
    this.fetchApps()
  },
  methods: {
    async fetchApps() {
      try {
        const [
          {
            data: { data: apps },
          },
        ] = await Promise.all([getAllApps()])
        this.$store.commit('apps/SET_APPS', transformApps(apps))
      } catch (err) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    getAppsFromCategory(category) {
      return this.allApps.filter((app) => app.category === category)
    },

    activateGridView({ category, label }) {
      this.gridView = true
      this.carouselView = false
      this.gridViewLabel = label || category
      this.gridViewSelectedCategory = category
    },

    activateCarouselView() {
      this.gridView = false
      this.carouselView = true
      this.gridViewLabel = null
      this.gridViewSelectedCategory = null
    },

    getCategoryOrder(category) {
      category = category.toLowerCase()
      const categoryOrder = {
        tools: 0,
        research: 1,
        marketplace: 2,
        bookmarks: 3,
      }
      return _.get(categoryOrder, category, 99)
    },
  },
}
</script>

<template>
  <Layout>
    <div :class="$style.viewContainer">
      <LoaderIcon v-if="loading || error" :error="error"></LoaderIcon>
      <div v-else>
        <div :class="$style.appsHeader" class="mb-3">
          <div v-if="!gridView" class="d-flex justify-content-between align-items-center">
            <BaseTitle>Apps</BaseTitle>
            <span :class="$style.linkAnchor" @click="activateGridView({ label: 'about the apps' })">
              About the apps
            </span>
          </div>
          <div v-if="gridView" :class="$style.appBreadcrumbs">
            <MottoIcon
              :class="$style.backButton"
              name="Arrow"
              icon-color="gray"
              class="cursor-pointer"
              @click.native="activateCarouselView" />
            <p class="p2 color-darkest-gray cursor-pointer" @click="activateCarouselView">APPS</p>
            <BaseIcon class="color-gray" name="chevron-right" />
            <p :class="$style.selectedBreadcrumb" class="button-text color-primary-blue">
              {{ gridViewLabel.toUpperCase() }}
            </p>
          </div>
        </div>

        <div v-if="!gridViewSelectedCategory">
          <CategorySelector
            :categories="getDistinctCategories"
            :allow-empty="true"
            :show-all-button="true"
            :selected-category.sync="selectedCategory" />
        </div>

        <div v-if="carouselView" :class="$style.gridViewFavoriteApps">
          <h3>Favorite {{ selectedCategory }} Apps</h3>
          <p v-if="!filteredFavoriteApps.length">Click the heart to favorite an app.</p>
          <transition-group>
            <AppBlock
              v-for="app in filteredFavoriteApps"
              :key="app.appId"
              :name="app.appName"
              :description="app.description"
              :category="app.category"
              :card-image="app.cardImage"
              :card-image-mobile="app.cardImageMobile"
              :app-url="app.appUrl"
              :app-id="app.appId" />
          </transition-group>
        </div>

        <div v-if="carouselView" :class="$style.carouselView">
          <AppCarousel
            v-for="category in getSelectedCategories"
            :key="category"
            :class="$style.appCarousel"
            :category="category"
            :apps="getAppsFromCategory(category)"
            @openGridView="activateGridView({ category })" />
        </div>
        <div
          v-if="gridView"
          :class="{
            [$style.gridView]: true,
            [$style.gridViewWithDescription]: isAboutTheAppsSelected,
          }">
          <transition-group name="fade">
            <AppBlock
              v-for="app in gridViewApps"
              :key="app.appId"
              :name="app.appName"
              :description="app.description"
              :category="app.category"
              :card-image="app.cardImage"
              :card-image-mobile="app.cardImageMobile"
              :app-url="app.appUrl"
              :app-id="app.appId"
              :options="{
                mobileCardSize: { width: '69', height: '69' },
                showHeart: false,
                showDescription: isAboutTheAppsSelected,
              }" />
          </transition-group>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.viewContainer {
  @media (max-width: $lg-breakpoint) {
    width: auto;
    min-width: unset;
  }

  width: auto;
}

.appsHeader {
  height: 50px;
}

.linkAnchor {
  font-size: 14px;
  font-weight: 700;
  color: $dark-gray;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: $darkest-gray;
  }
}

.appBreadcrumbs {
  display: flex;
  align-items: center;

  > svg {
    margin: -2px 13px 0;
  }

  .backButton {
    margin-right: 29px;

    > svg {
      width: 30px;
      height: 30px;
    }
  }
}

.selectedBreadcrumb {
  &::after {
    float: left;
    width: 100%;
    height: 4px;
    content: '';
    background: $primary-blue;
    border-radius: 5px;
  }
}

.appCarousel {
  margin-top: 40px;
}

.carouselView {
  margin-top: 40px;
}

.gridViewFavoriteApps {
  margin-top: 40px;

  h3 {
    margin-bottom: 24px;
  }

  p {
    color: $dark-gray;
    text-align: center;
  }

  > span {
    @media (max-width: $lg-breakpoint) {
      grid-template-columns: repeat(auto-fill, 98px);
      grid-gap: 32px 16px;
    }

    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(193px, 1fr));
    grid-gap: 32px 24px;
    margin-top: 0;
  }
}

.gridView {
  > span {
    @media (max-width: $lg-breakpoint) {
      grid-template-columns: repeat(auto-fill, 69px);
      grid-gap: 32px 16px;
    }

    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(193px, 1fr));
    grid-gap: 32px 24px;
    margin-top: 40px;
  }

  &.gridViewWithDescription > span {
    @media (max-width: $lg-breakpoint) {
      grid-template-columns: unset;
      grid-gap: 40px;
    }
  }
}
</style>
